import React, { useState, useEffect } from "react";
import { Edit2, Loader2, X, Check,  AlertCircle } from 'lucide-react';
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  FiArrowLeft,
  FiTrash2,
  FiDollarSign,
  FiPackage,
  FiTrendingUp,
} from "react-icons/fi";
import { authAPI } from "../../services/api";

export default function ProductView() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toast, setToast] = useState({ show: false, type: '', message: '' });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "",
    category_id: "",
    price: "",
    unit: "",
    quantity: "",
    description: ""
  });
  const [categories, setCategories] = useState([]);

  const units = [
    "kg", "lb", "oz", "bunch", "each", "dozen", "jar", "basket", "pint", "gallon"
  ];

  const showToast = (type, message) => {
    setToast({ show: true, type, message });
    setTimeout(() => {
      setToast({ show: false, type: '', message: '' });
    }, 3000);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [productResult, categoriesResult] = await Promise.all([
          authAPI.getProduct(productId),
          authAPI.getCategories()
        ]);
        
        if (categoriesResult) {
          setCategories(categoriesResult);
        }
        
        if (productResult.status && productResult.data) {
          const productData = productResult.data;
          setProduct(productData);
          
          // Find category_id by matching category_name
          const category = categoriesResult.find(cat => cat.name === productData.category_name);
          
          setEditForm({
            name: productData.product_name,
            category_id: category ? category.id : "",
            price: productData.amount,
            unit: productData.unit,
            quantity: productData.quantity,
            description: productData.description
          });
        } else {
          setProduct(null);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        showToast('error', 'Failed to load data');
        setProduct(null);
      } finally {
        setLoading(false);
      }
    };

    if (productId) {
      fetchData();
    }
  }, [productId]);

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    try {
      setIsUpdating(true);
      
      const updateData = {
        category_id: parseInt(editForm.category_id),
        quantity: parseInt(editForm.quantity),
        amount: parseFloat(editForm.price),
        description: editForm.description,
        unit: editForm.unit,
        product_name: editForm.name
      };

      const result = await authAPI.updateProduct(productId, updateData);

      if (result.status) {
        showToast('success', result.message || 'Product updated successfully');
        
        // Update local state
        setProduct(prev => ({
          ...prev,
          ...updateData,
          product_name: updateData.product_name,
          amount: updateData.amount.toString()
        }));
        
        setIsEditing(false);
      } else {
        showToast('error', result.message || 'Failed to update product');
      }
    } catch (error) {
      console.error('Error updating product:', error);
      showToast('error', 'Failed to update product');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      const result = await authAPI.deleteProduct(productId);

      if (result.status) {
        showToast('success', 'Product deleted successfully');
        navigate('/farmer-dashboard/sell');
      } else {
        showToast('error', result.message || 'Failed to delete product');
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      showToast('error', 'Failed to delete product');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  if (loading) {
    return (
      <div className="product-view-container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-not-found">
        <h2>Product Not Found</h2>
        <Link to="/farmer-dashboard/sell" className="back-link">
          <FiArrowLeft /> Return to Sell Page
        </Link>
      </div>
    );
  }

  return (
    <div className="product-view-container">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`toast-notification-fxk ${toast.type === 'success' ? 'toast-success-fxk' : 'toast-error-fxk'}`}>
          <div className="toast-content-fxk">
            <div className="toast-icon-fxk">
              {toast.type === 'success' ? '✓' : '✗'}
            </div>
            <div className="toast-message-fxk">{toast.message}</div>
            <button 
              className="toast-close-fxk"
              onClick={() => setToast({ show: false, type: '', message: '' })}
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      <div className="product-view-header">
        <Link to="/farmer-dashboard/sell" className="dash-back">
          <FiArrowLeft /> <p>Back</p>
        </Link>
        <div className="product-actions">
          {isEditing ? (
            <div className="edit-actions">
              <button
                className="save-btn"
                onClick={handleSave}
                disabled={isUpdating}
              >
                {isUpdating ? (
                  <>
                    <Loader2 size={16} className="loading" /> Saving...
                  </>
                ) : (
                  <>
                    <Check size={16} /> Save
                  </>
                )}
              </button>
              <button
                className="cancel-btn"
                onClick={() => setIsEditing(false)}
                disabled={isUpdating}
              >
                <X size={16} /> Cancel
              </button>
            </div>
          ) : (
            <button
              className="edit-btn"
              onClick={() => setIsEditing(true)}
            >
              <Edit2 size={16} /> Edit
            </button>
          )}
          <button 
            className="delete-btn" 
            onClick={() => setShowDeleteModal(true)}
            disabled={isDeleting}
          >
            <FiTrash2 /> Delete
          </button>
        </div>
      </div>

      <div className="product-main-content">
        <div className="product-image-section">
          <div className="main-image">
            <img
              src={product.product_image}
              alt={product.product_name}
            />
          </div>
        </div>

        <div className="product-details-section">
          {isEditing ? (
            <div className="edit-form">
              <div className="form-group">
                <label>Product Name</label>
                <input
                  type="text"
                  name="name" disabled={true}
                  value={editForm.name}
                  onChange={handleEditChange}
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label>Category</label>
                <select
                  name="category_id"
                  value={editForm.category_id}
                  onChange={handleEditChange}
                  className="form-select"
                >
                  <option value="">Select Category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Price ($)</label>
                <input
                  type="number"
                  name="price"
                  value={editForm.price}
                  onChange={handleEditChange}
                  className="form-input"
                  min="0.01"
                  step="0.01"
                />
              </div>

              <div className="form-group">
                <label>Unit</label>
                <select
                  name="unit"
                  value={editForm.unit}
                  onChange={handleEditChange}
                  className="form-select"
                >
                  {units.map((unit) => (
                    <option key={unit} value={unit}>
                      {unit}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Quantity</label>
                <input
                  type="number"
                  name="quantity"
                  value={editForm.quantity}
                  onChange={handleEditChange}
                  className="form-input"
                  min="1"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={editForm.description}
                  onChange={handleEditChange}
                  className="form-textarea"
                  rows="3"
                />
              </div>
            </div>
          ) : (
            <>
              <h1 className="product-title">{product.product_name}</h1>
              <div className="product-meta">
                <span className="product-category">{product.category_name}</span>
                <span className="product-status in-stock">
                  In Stock
                </span>
              </div>

              <div className="product-price">
                <h2>₦{parseFloat(product.amount).toFixed(2)}/{product.unit}</h2>
              </div>

              <div className="product-description">
                <h3>Description</h3>
                <p>{product.description}</p>
              </div>

              <div className="product-metrics">
                <div className="metric-card">
                  <div className="metric-icon">
                    <FiPackage />
                  </div>
                  <div className="metric-info">
                    <h4>Available Quantity</h4>
                    <p>{product.quantity} {product.unit}</p>
                  </div>
                </div>
                <div className="metric-card">
                  <div className="metric-icon">
                    <FiDollarSign />
                  </div>
                  <div className="metric-info">
                    <h4>Price</h4>
                    <p>₦{parseFloat(product.amount).toFixed(2)}/{product.unit}</p>
                  </div>
                </div>
              </div>

              <div className="additional-details">
                <div className="detail-item">
                  <h4>Category</h4>
                  <p>{product.category_name}</p>
                </div>
                <div className="detail-item">
                  <h4>Date Created</h4>
                  <p>{new Date(product.created_at).toLocaleDateString()}</p>
                </div>
                <div className="detail-item">
                  <h4>Last Updated</h4>
                  <p>{new Date(product.updated_at).toLocaleDateString()}</p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-content delete-modal">
            <div className="modal-header">
              <h3>Delete Product</h3>
              <button
                className="close-button"
                onClick={() => setShowDeleteModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="delete-modal-content">
              <div className="delete-warning">
                <AlertCircle size={48} className="warning-icon" />
                <h4>Are you sure you want to delete this product?</h4>
                <p>This action cannot be undone.</p>
              </div>
              
              <div className="product-summary">
                <img 
                  src={product.product_image} 
                  alt={product.product_name}
                  className="product-thumbnail"
                />
                <div className="product-info">
                  <h5>{product.product_name}</h5>
                  <p className="product-category">{product.category_name}</p>
                  <p className="product-price">₦{parseFloat(product.amount).toFixed(2)}/{product.unit}</p>
                </div>
              </div>
              
              <div className="delete-actions">
                <button
                  className="cancel-btn"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  className="delete-confirm-btn"
                  onClick={handleDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <Loader2 size={16} className="loading" /> Deleting...
                    </>
                  ) : (
                    <>
                      <FiTrash2 size={16} /> Delete Product
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .toast-notification-fxk {
          position: fixed;
          top: 20px;
          right: 20px;
          z-index: 9999;
          max-width: 400px;
          animation: slideInRight 0.3s ease-out;
        }

        .toast-success-fxk {
          background: #10b981;
          color: white;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .toast-error-fxk {
          background: #ef4444;
          color: white;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .toast-content-fxk {
          display: flex;
          align-items: center;
          padding: 16px;
          gap: 12px;
        }

        .toast-icon-fxk {
          font-size: 18px;
          font-weight: bold;
          flex-shrink: 0;
        }

        .toast-message-fxk {
          flex: 1;
          font-size: 14px;
          line-height: 1.4;
        }

        .toast-close-fxk {
          background: none;
          border: none;
          color: white;
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          transition: background-color 0.2s;
          flex-shrink: 0;
        }

        .toast-close-fxk:hover {
          background: rgba(255, 255, 255, 0.1);
        }

        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        .edit-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .form-group label {
          font-weight: 600;
          color: #374151;
        }

        .form-input, .form-select, .form-textarea {
          padding: 12px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 14px;
          transition: border-color 0.2s;
        }

        .form-input:focus, .form-select:focus, .form-textarea:focus {
          outline: none;
          border-color: #3b82f6;
        }

        .form-textarea {
          resize: vertical;
          min-height: 80px;
        }

        .edit-actions {
          display: flex;
          gap: 12px;
        }

        .save-btn, .cancel-btn {
          padding: 8px 16px;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: all 0.2s;
        }

        .save-btn {
          background: #10b981;
          color: white;
        }

        .save-btn:hover:not(:disabled) {
          background: #059669;
        }

        .cancel-btn {
          background: #6b7280;
          color: white;
        }

        .cancel-btn:hover:not(:disabled) {
          background: #4b5563;
        }

        button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .loading {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal-content {
          background: white;
          border-radius: 12px;
          max-width: 500px;
          width: 90%;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid #e5e7eb;
        }

        .modal-header h3 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: #1f2937;
        }

        .close-button {
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          transition: background-color 0.2s;
        }

        .close-button:hover {
          background: #f3f4f6;
        }

        .delete-modal {
          max-width: 500px;
        }

        .delete-modal-content {
          padding: 20px 24px;
        }

        .delete-warning {
          text-align: center;
          margin-bottom: 24px;
        }

        .warning-icon {
          color: #ef4444;
          margin-bottom: 16px;
        }

        .delete-warning h4 {
          color: #1f2937;
          margin-bottom: 8px;
          font-size: 18px;
        }

        .delete-warning p {
          color: #6b7280;
          font-size: 14px;
        }

        .product-summary {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 16px;
          background: #f9fafb;
          border-radius: 8px;
          margin-bottom: 24px;
        }

        .product-thumbnail {
          width: 60px;
          height: 60px;
          object-fit: cover;
          border-radius: 8px;
        }

        .product-info h5 {
          margin: 0 0 4px 0;
          color: #1f2937;
          font-size: 16px;
        }

        .product-category {
          margin: 0 0 4px 0;
          color: #6b7280;
          font-size: 14px;
        }

        .product-price {
          margin: 0;
          color: #059669;
          font-weight: 600;
          font-size: 14px;
        }

        .delete-actions {
          display: flex;
          gap: 12px;
          margin: 0px 10px;
          justify-content: flex-end;
        }

        .delete-confirm-btn {
          background: #ef4444;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 6px;
          font-size: 14px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: background-color 0.2s;
        }

        .delete-confirm-btn:hover:not(:disabled) {
          background: #dc2626;
        }

        .delete-confirm-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
}